import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendEmailVerification, reload } from 'firebase/auth';
import { auth } from '../config/firebase';
import '../styles/VerifyEmailPage.css';

export const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);

  const firebaseUser = auth.currentUser;

  const handleResendEmail = async () => {
    if (!firebaseUser) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResendSuccess(false);

      await sendEmailVerification(firebaseUser, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: true,
      });

      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationComplete = async () => {
    if (!firebaseUser) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Reload user to get latest verification status
      await reload(firebaseUser);

      if (firebaseUser.emailVerified) {
        // Email is verified, redirect to login
        navigate('/login', { replace: true });
      } else {
        setError('Email not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="verify-email-container">
      <div className="verify-card">
        <div className="verify-header">
          <div className="verify-icon">✉️</div>
          <h1>Verify Your Email</h1>
        </div>

        <div className="verify-content">
          <p className="verify-message">
            We've sent a verification link to:
          </p>
          <p className="email-address">
            <strong>{firebaseUser?.email}</strong>
          </p>

          <div className="verify-steps">
            <p className="steps-title">What to do next:</p>
            <ol>
              <li>Check your email inbox (and spam folder)</li>
              <li>Click the verification link in the email</li>
              <li>Return here and click "I've Verified My Email"</li>
            </ol>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {resendSuccess && (
            <div className="success-message">
              Verification email sent! Check your inbox.
            </div>
          )}
        </div>

        <div className="verify-actions">
          <button
            onClick={handleVerificationComplete}
            className="verify-button"
            disabled={loading}
          >
            {loading ? 'Checking...' : "I've Verified My Email"}
          </button>

          <button
            onClick={handleResendEmail}
            className="resend-button"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Resend Verification Email'}
          </button>
        </div>

        <p className="verify-note">
          Verification emails can take a few minutes to arrive. Check your spam folder if you don't see it.
        </p>
      </div>
    </div>
  );
};
